import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

// ---------------------------------------------------------------------------
// Reusable Excel template logic for Performance templates
// (Appraisal, PIP, Exit Interview).
//
// Everything lives in a SINGLE sheet ("Template") laid out as labelled blocks
// (TEMPLATE INFO / SECTIONS / RATING OPTIONS, plus EXIT REASONS for Exit
// Interview) so the whole template is visible at once and round-trips cleanly
// on import.
// ---------------------------------------------------------------------------

export const PERFORMANCE_TEMPLATE_TYPES = ["Appraisal", "PIP", "Exit Interview"];

// --- Default Data Sets (sample content shipped inside the downloaded file) ---

const APPRAISAL_DEFAULT_SECTIONS = [
  {
    title: "Core Values",
    description: "Evaluate the employee's adherence to company core values.",
    weight: 50,
    questions: [
      "Attention to detail in work outputs",
      "Ability to meet project specifications and deadlines",
      "Consistency in maintaining high-quality work",
    ],
  },
  {
    title: "Professional Skills",
    description: "Assess technical and professional capabilities.",
    weight: 50,
    questions: [
      "Technical knowledge required for the role",
      "Problem-solving capabilities",
    ],
  },
];

const EXIT_DEFAULT_SECTIONS = [
  {
    title: "Role & Job Satisfaction",
    description: "Feedback on the actual role versus expectations.",
    weight: 20,
    questions: [
      "My role matched what was promised during hiring",
      "My workload was manageable",
      "My skills were fully utilized",
    ],
  },
  {
    title: "Management & Leadership",
    description: "Feedback on reporting manager and leadership.",
    weight: 20,
    questions: [
      "My manager supported my professional growth",
      "Performance evaluations were fair",
    ],
  },
  {
    title: "Compensation & Benefits",
    description: "Satisfaction with pay and perks.",
    weight: 20,
    questions: [
      "Compensation was fair for my responsibilities",
      "Benefits met my expectations",
    ],
  },
  {
    title: "Work Culture",
    description: "Environment and team dynamics.",
    weight: 20,
    questions: [
      "I felt comfortable raising concerns",
      "Team collaboration was effective",
    ],
  },
  {
    title: "Career Growth",
    description: "Opportunities for advancement.",
    weight: 20,
    questions: ["Clear career path existed", "Training opportunities were sufficient"],
  },
];

const PIP_DEFAULT_SECTIONS = [
  {
    title: "Performance Gap Areas",
    description: "Identify specific areas where performance falls below expectations.",
    weight: 25,
    questions: [
      "Quality of deliverables meets the required standard",
      "Adherence to deadlines and timelines",
      "Accuracy and attention to detail in assigned tasks",
    ],
  },
  {
    title: "SMART Goals Progress",
    description: "Evaluate progress toward specific, measurable improvement goals.",
    weight: 30,
    questions: [
      "Goal completion rate within defined timeframe",
      "Demonstrates measurable improvement in KPIs",
      "Takes initiative to seek feedback and course-correct",
    ],
  },
  {
    title: "Behavioral & Professional Conduct",
    description: "Assess attitude, accountability, and professional behavior.",
    weight: 20,
    questions: [
      "Attendance and punctuality during PIP period",
      "Responsiveness to feedback and coaching",
      "Collaboration and communication with team",
    ],
  },
  {
    title: "Support Utilization",
    description: "Evaluate how effectively the employee uses provided resources.",
    weight: 15,
    questions: [
      "Engagement with assigned training programs",
      "Utilization of mentorship and check-in sessions",
    ],
  },
  {
    title: "Final Evaluation Criteria",
    description: "Overall assessment for PIP outcome determination.",
    weight: 10,
    questions: [
      "Overall readiness to return to standard performance expectations",
      "Sustained improvement observed (not just during review periods)",
    ],
  },
];

const APPRAISAL_RATING_OPTIONS = [
  "Outstanding",
  "Exceeds Expectations",
  "Meets Expectations",
  "Needs Improvement",
  "Unacceptable",
];

const EXIT_RATING_OPTIONS = [
  "Strongly Disagree",
  "Disagree",
  "Neutral",
  "Agree",
  "Strongly Agree",
];

const PIP_RATING_OPTIONS = [
  "Significantly Exceeded",
  "Exceeded Target",
  "Met Target",
  "Partial Progress",
  "No Improvement",
];

const DEFAULT_EXIT_REASONS = [
  "Better Compensation",
  "Career Growth",
  "Higher Education",
  "Relocation",
  "Work-Life Balance",
  "Managerial Issues",
  "Work Culture",
  "Health / Personal",
  "Company Stability",
  "Other",
];

const TEMPLATE_DEFAULTS = {
  Appraisal: {
    name: "Appraisal Template",
    sections: APPRAISAL_DEFAULT_SECTIONS,
    ratingOptions: APPRAISAL_RATING_OPTIONS,
  },
  PIP: {
    name: "PIP Template",
    sections: PIP_DEFAULT_SECTIONS,
    ratingOptions: PIP_RATING_OPTIONS,
  },
  "Exit Interview": {
    name: "Exit Interview Template",
    sections: EXIT_DEFAULT_SECTIONS,
    ratingOptions: EXIT_RATING_OPTIONS,
    exitReasons: DEFAULT_EXIT_REASONS,
  },
};

const SHEET_NAME = "Template";

// Block markers placed in column A so the parser can find each section.
const MARKERS = {
  INFO: "TEMPLATE INFO",
  SECTIONS: "SECTIONS",
  RATING: "RATING OPTIONS",
  EXIT_REASONS: "EXIT REASONS",
};

const cell = (value) =>
  value === undefined || value === null ? "" : value.toString().trim();

// ---------------------------------------------------------------------------
// DOWNLOAD: build a single-sheet, fillable Excel template for the given type.
// ---------------------------------------------------------------------------

export const downloadPerformanceTemplate = async (type) => {
  if (!PERFORMANCE_TEMPLATE_TYPES.includes(type)) {
    throw new Error(`Unknown template type: ${type}`);
  }

  const defaults = TEMPLATE_DEFAULTS[type];
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(SHEET_NAME);

  sheet.getColumn(1).width = 34;
  sheet.getColumn(2).width = 52;
  sheet.getColumn(3).width = 14;
  sheet.getColumn(4).width = 58;

  const addMarker = (text) => {
    const row = sheet.addRow([text]);
    row.getCell(1).font = { bold: true, size: 12, color: { argb: "FF1F4E79" } };
    row.getCell(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9E1F2" },
    };
    return row;
  };

  const addColumnHeader = (cols) => {
    const row = sheet.addRow(cols);
    row.font = { bold: true };
    row.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE0E0E0" },
    };
    return row;
  };

  const blank = () => sheet.addRow([]);

  // --- TEMPLATE INFO ---
  addMarker(MARKERS.INFO);
  sheet.addRow(["Template Name", defaults.name]);
  const typeRow = sheet.addRow(["Type", type]);
  blank();

  // Lock the Type cell to the known values via a dropdown so it can't be
  // edited to an arbitrary value in Excel.
  typeRow.getCell(2).dataValidation = {
    type: "list",
    allowBlank: false,
    formulae: [`"${PERFORMANCE_TEMPLATE_TYPES.join(",")}"`],
    showErrorMessage: true,
    errorStyle: "error",
    errorTitle: "Invalid Type",
    error: "Please select a template type from the dropdown.",
  };

  // --- SECTIONS (grouped: Section Title/Description/Weight are written ONCE per
  // section — on its first question row — and left blank for that section's
  // remaining questions; a blank row separates sections. The importer forward-
  // fills the blanks back onto the section, so this round-trips cleanly. ---
  addMarker(MARKERS.SECTIONS);
  const sectionsHeader = addColumnHeader([
    "Section Title",
    "Section Description",
    "Weight",
    "Question",
  ]);
  sectionsHeader.getCell(1).note =
    "Fill Section Title, Section Description and Weight only on the FIRST question row of each section. Leave them blank for that section's other questions, and separate sections with a blank row.";
  defaults.sections.forEach((sec, sectionIndex) => {
    if (sectionIndex > 0) blank(); // visual gap between sections
    const questions = sec.questions.length ? sec.questions : [""];
    questions.forEach((q, questionIndex) => {
      sheet.addRow(
        questionIndex === 0
          ? [sec.title, sec.description, sec.weight, q]
          : ["", "", "", q]
      );
    });
  });
  blank();

  // --- RATING OPTIONS ---
  addMarker(MARKERS.RATING);
  addColumnHeader(["Rating Option"]);
  defaults.ratingOptions.forEach((opt) => sheet.addRow([opt]));
  blank();

  // --- Type-specific blocks ---
  if (type === "Exit Interview") {
    addMarker(MARKERS.EXIT_REASONS);
    addColumnHeader(["Exit Reason"]);
    defaults.exitReasons.forEach((reason) => sheet.addRow([reason]));
    blank();
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${type.replace(/\s+/g, "_")}_Template.xlsx`);
};

// ---------------------------------------------------------------------------
// IMPORT: read + validate the single-sheet Excel file for the given type,
// returning a ready-to-preview template payload.
//   -> { valid: boolean, errors: string[], template: object|null }
// ---------------------------------------------------------------------------

export const parsePerformanceTemplateFile = async (file, type) => {
  const errors = [];

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer);

  // Prefer the "Template" sheet; fall back to the first sheet in the file.
  const ws =
    workbook.Sheets[SHEET_NAME] || workbook.Sheets[workbook.SheetNames[0]];
  if (!ws) {
    return {
      valid: false,
      errors: ["The Excel file has no readable sheet."],
      template: null,
    };
  }

  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const markerSet = new Set(Object.values(MARKERS));

  let templateName = "";
  let fileType = "";
  const sectionMap = new Map();
  const ratingOptions = [];
  const exitReasons = [];

  let current = null;
  let headerSeen = false; // skip the column-header row that follows table markers
  let currentSectionTitle = null; // forward-fill the section title across rows

  for (const raw of rows) {
    const first = cell(raw[0]);
    const upper = first.toUpperCase();

    if (markerSet.has(upper)) {
      current = upper;
      headerSeen = false;
      currentSectionTitle = null;
      continue;
    }
    if (!current) continue;
    if (raw.every((c) => cell(c) === "")) continue; // blank separator row

    if (current === MARKERS.INFO) {
      const key = first.toLowerCase();
      const val = cell(raw[1]);
      if (key === "template name") templateName = val;
      else if (key === "type") fileType = val;
      continue;
    }

    // First non-empty row after a table marker is its column header — skip it.
    if (!headerSeen) {
      headerSeen = true;
      continue;
    }

    if (current === MARKERS.SECTIONS) {
      const title = first;
      const description = cell(raw[1]);
      const weight = raw[2];
      const question = cell(raw[3]);
      if (!title && !question) continue;

      if (title) {
        // A new (or repeated) section title starts/continues a section.
        currentSectionTitle = title;
        if (!sectionMap.has(title)) {
          sectionMap.set(title, { title, description, weight, questions: [] });
        }
        const sec = sectionMap.get(title);
        if (!sec.description && description) sec.description = description;
        if (
          (sec.weight === "" || sec.weight === undefined) &&
          weight !== "" &&
          weight !== undefined
        ) {
          sec.weight = weight;
        }
        if (question) sec.questions.push(question);
      } else if (question && currentSectionTitle) {
        // Title left blank: belongs to the most recent section above it.
        sectionMap.get(currentSectionTitle).questions.push(question);
      }
    } else if (current === MARKERS.RATING) {
      if (first) ratingOptions.push(first);
    } else if (current === MARKERS.EXIT_REASONS) {
      if (first) exitReasons.push(first);
    }
  }

  // --- Validation ---
  if (fileType && fileType.toLowerCase() !== type.toLowerCase()) {
    errors.push(
      `This file is a "${fileType}" template, but you selected "${type}". Please pick the matching option.`
    );
  }

  const sections = [...sectionMap.values()];
  if (sections.length === 0) {
    errors.push(
      "No sections found. Use the downloaded template and fill in the SECTIONS block."
    );
  }
  sections.forEach((sec, i) => {
    const label = sec.title || `#${i + 1}`;
    if (!sec.title) errors.push(`Section #${i + 1} is missing a title.`);
    if (sec.questions.length === 0) errors.push(`Section "${label}" has no questions.`);
    const w = Number(sec.weight);
    if (sec.weight === "" || sec.weight === undefined || Number.isNaN(w)) {
      errors.push(`Section "${label}" has an invalid weight (must be a number).`);
    }
  });

  if (ratingOptions.length < 2) {
    errors.push("At least 2 rating options are required.");
  }

  if (type === "Exit Interview" && exitReasons.length === 0) {
    errors.push("At least one exit reason is required for Exit Interview templates.");
  }

  const template = {
    name: templateName || `${type} Template`,
    type,
    sections: sections.map((sec, i) => ({
      id: `sec_${i + 1}`,
      title: sec.title,
      description: sec.description,
      weight: Number(sec.weight) || 0,
      questions: sec.questions.map((text, j) => ({
        id: `q_${i + 1}_${j + 1}`,
        text,
      })),
    })),
    ratingOptions,
    exitReasons: type === "Exit Interview" ? exitReasons : [],
  };

  return { valid: errors.length === 0, errors, template };
};
